import { defineComponent as _defineComponent } from 'vue'
import { capitalizeString } from "@/helpers/transformation/string";
  import EOIRequestForm from "@/components/eoi/EOIRequestForm.vue";
  import { EoiTemplateFields } from "@/enums/EoiTemplateFields";
  import { EoiTemplateGroups } from "@/enums/EoiTemplateGroups";
  import { ref, computed, onBeforeMount, watch, reactive } from "vue";
  import ToggleButton from "@/components/ui/ToggleButton.vue";
  import CheckboxInput from "@/components/ui/input/CheckboxInput.vue";
  import router from "@/router";
  import getUserRole from "@/helpers/getUserRole";
  import store from "@/store";
  import { EstateInterface } from "@/types/EstateInterface";
  import { Business } from "@/types/BusinessInterface";
  import { CustomTerms } from "@/types/CustomTerms";
  import { AxiosResponse } from "axios";

  type EoiField = {
    isActive: boolean;
    fieldName: string;
    isRequired: boolean;
  };

  
export default /*@__PURE__*/_defineComponent({
  __name: 'EOIEditForm',
  setup(__props, { expose: __expose }) {
  __expose();

  const getGroups = computed(() => {
    return (
      form.value.groups?.filter(
        ({ groupName }) =>
          groupName !== EoiTemplateGroups.PURCHASE_AS_COMPANY_DETAILS
      ) || []
    );
  });
  const estateId = computed(() => {
    return store.state.user.selectedEstateId;
  });

  const isPreviewActive = ref(false);

  const checkboxLabelText = (fieldName: string) => {
    if (fieldName === EoiTemplateFields.CONTRACT_SUBJECT_TO_FINANCE) {
      return "Will you be getting a loan for this purchase?";
    }

    return fieldName;
  };

  const subjectToFinanceCondition = computed(() => {
    const groups = form.value?.groups;

    if (!groups) return false;

    const contractConditionsGroup = groups.find(
      ({ groupName }) => groupName === EoiTemplateGroups.CONTRACT_CONDITIONS
    );

    const contractSubjectToFinanceField = contractConditionsGroup.fields.find(
      (field) =>
        field.fieldName === EoiTemplateFields.CONTRACT_SUBJECT_TO_FINANCE
    );

    return contractSubjectToFinanceField.isActive;
  });

  const form = ref({});
  const loading = ref(true);
  const estate = ref<EstateInterface | null>(null);
  const userBusiness = ref<Business | null>(null);
  const customTermsEnabled = ref(false);
  const customTermsForm = reactive<CustomTerms>({
    text: "",
    showOnlyCustomTerms: false,
  });
  const { isLandownerRole } = getUserRole();

  onBeforeMount(() => {
    Promise.all([getEoiTemplate(), getEstate(), getUserBusiness()]).finally(
      () => {
        setCustomTermsForm();
        loading.value = false;
      }
    );
  });

  const getEoiTemplate = () => {
    return store
      .dispatch("eoi/getCustomTemplate", estateId.value)
      .then((res) => {
        if (res.data) {
          setFormTemplate(res.data);
          return res.data;
        }

        return store.dispatch("eoi/getDefaultTemplate").then((response) => {
          setFormTemplate(response.data);
        });
      });
  };

  const setFormTemplate = (template: Record<string, any>) => {
    form.value = {
      ...template,
      requestAmlInformation: template.requestAmlInformation !== false,
    };
    const companyDetailsGroup = form.value.groups?.find(
      ({ groupName }) =>
        groupName === EoiTemplateGroups.PURCHASE_AS_COMPANY_DETAILS
    );

    companyDetailsGroup?.fields.forEach(
      (field: EoiField) => (field.isActive = true)
    );
    setFieldForToggleToForm();
  };

  const getEstate = () => {
    return store.dispatch("estate/getEstate", estateId.value).then((res) => {
      estate.value = res.data;
    });
  };

  const getUserBusiness = () => {
    return store
      .dispatch("business/getBusinessByLandownerId")
      .then((res: AxiosResponse<Business>) => {
        userBusiness.value = res.data;
      })
      .catch(() => {
        userBusiness.value = null;
      });
  };

  watch(
    () => subjectToFinanceCondition.value,
    (value) => {
      if (!value) {
        toggleSubjectFinanceFields();
      }
    }
  );

  const toggleSubjectFinanceFields = () => {
    const { FINANCE_BROKER_DETAILS, LAND_LOAN_AMOUNT } = EoiTemplateFields;
    const fieldNamesToDeactivate = [FINANCE_BROKER_DETAILS, LAND_LOAN_AMOUNT];

    const contractConditionsGroup = form.value.groups.find(
      ({ groupName }) => groupName === EoiTemplateGroups.CONTRACT_CONDITIONS
    );

    if (!contractConditionsGroup) return;

    contractConditionsGroup.fields
      .filter((field) => fieldNamesToDeactivate.includes(field.fieldName))
      .forEach((field: EoiField) => (field.isActive = false));
  };

  const getCheckboxClasses = (fieldName: string | EoiTemplateFields) => {
    const { LAND_LOAN_AMOUNT, FINANCE_BROKER_DETAILS } = EoiTemplateFields;
    const isSpecialField = [LAND_LOAN_AMOUNT, FINANCE_BROKER_DETAILS].includes(
      fieldName
    );
    const isSubjectToFinance = subjectToFinanceCondition.value;

    return [
      { "nested-checkbox": isSpecialField },
      { "nested-checkbox-hidden": !isSubjectToFinance && isSpecialField },
    ];
  };
  const save = () => {
    loading.value = true;
    const saveTemplate =
      form.value.templateType === "custom"
        ? store.dispatch("eoi/updateCustomTemplate", {
            templateId: form.value._id,
            groups: form.value.groups,
            requestAmlInformation: form.value.requestAmlInformation,
          })
        : (() => {
            form.value.landownerId = getLandownerId();
            form.value.estateId = estateId;
            delete form.value._id;
            return store.dispatch("eoi/createCustomTemplate", form.value);
          })();

    Promise.all([saveTemplate, saveCustomTerms()])
      .then(() => {
        router.push({ name: "EOI" });
      })
      .finally(() => {
        loading.value = false;
      });
  };

  const saveCustomTerms = () => {
    return store.dispatch("estate/updateCustomTerms", {
      id: estateId.value,
      data: {
        customTerms: {
          text: customTermsEnabled.value ? customTermsForm.text : "",
          showOnlyCustomTerms:
            customTermsEnabled.value && customTermsForm.showOnlyCustomTerms,
        },
      },
    });
  };

  const setCustomTermsForm = () => {
    const terms = isEstateBusinessOwner.value
      ? estate.value?.customTerms
      : estate.value?.collaboratorCustomTerms?.find(
          (item) => item.businessId === userBusiness.value?._id
        )?.customTerms;

    customTermsForm.text = terms?.text || "";
    customTermsForm.showOnlyCustomTerms = !!terms?.showOnlyCustomTerms;
    customTermsEnabled.value = !!terms?.text;
  };

  const isEstateBusinessOwner = computed(() => {
    return estate.value?.landownerId?._id === store.state.user.data?._id;
  });

  const getLandownerId = () => {
    return isLandownerRole.value
      ? store.state.user.data._id
      : store.state.user.data.detailsId?.landownerId;
  };

  const switchToggle = (isSelectedFields: boolean, index: number) => {
    getGroups.value[index].fields.forEach((field: any) => {
      field.isActive = isSelectedFields;
    });
    getGroups.value[index].isSelectedFields = isSelectedFields;
  };

  const setFieldForToggleToForm = () => {
    form.value.groups.forEach((item: any) => {
      const togglePosition =
        item.fields.filter((field: any) => field.isActive).length > 0;

      item.isSelectedFields = togglePosition;
    });
  };

  const setDefaultTemplate = () => {
    form.value.requestAmlInformation = true;
    form.value.groups.forEach((group: any) => {
      group.isSelectedFields = true;
      group.fields.forEach((field: any) => {
        field.isActive = true;
      });
    });
  };

  const checkIsSelectedFields = (group: any) => {
    if (group?.fields.filter((field: any) => field.isActive).length > 0) {
      group.isSelectedFields = true;
    } else {
      group.isSelectedFields = false;
    }
  };

const __returned__ = { getGroups, estateId, isPreviewActive, checkboxLabelText, subjectToFinanceCondition, form, loading, estate, userBusiness, customTermsEnabled, customTermsForm, isLandownerRole, getEoiTemplate, setFormTemplate, getEstate, getUserBusiness, toggleSubjectFinanceFields, getCheckboxClasses, save, saveCustomTerms, setCustomTermsForm, isEstateBusinessOwner, getLandownerId, switchToggle, setFieldForToggleToForm, setDefaultTemplate, checkIsSelectedFields, get capitalizeString() { return capitalizeString }, EOIRequestForm, get EoiTemplateFields() { return EoiTemplateFields }, get EoiTemplateGroups() { return EoiTemplateGroups }, ref, computed, onBeforeMount, watch, reactive, ToggleButton, CheckboxInput, get router() { return router }, get getUserRole() { return getUserRole }, get store() { return store }, get EstateInterface() { return EstateInterface }, get Business() { return Business }, get CustomTerms() { return CustomTerms }, get AxiosResponse() { return AxiosResponse } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})